import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, CalendarCheck, Globe, LogIn, Check } from 'lucide-react';
import TribeBanner from './TribeBanner';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../lib/auth';

const LANGS = [
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'zh', label: '中文', short: '中文' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();
  const nav = useNavigate();
  const { t, lang, setLang } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    if (!langOpen) return undefined;
    const onClick = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [langOpen]);

  const goToBook = (e) => {
    e.preventDefault();
    if (user) nav('/book');
    else nav('/login?next=/book');
  };

  const navLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.specialists'), path: '/specialists' },
    { label: t('nav.cases'), path: '/cases' },
    { label: t('nav.blog'), path: '/blog' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <TribeBanner />
      <div
        className={`bg-white/95 backdrop-blur-md border-b transition-all ${
          scrolled ? 'border-[#e9e2d6] shadow-sm' : 'border-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-6 h-[78px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="flex flex-col leading-none">
              <span className="font-[Plus_Jakarta_Sans] font-extrabold text-[20px] tracking-tight text-[#14110f]">
                WYSE<span className="text-[#f5b300]">Tribe</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">{t('brand.sub')}</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => {
              const active = location.pathname === l.path;
              return (
                <Link
                  key={l.path}
                  to={l.path}
                  className={`px-4 py-2 text-[15px] font-medium transition-colors relative ${
                    active ? 'text-[#f5b300]' : 'text-[#14110f] hover:text-[#f5b300]'
                  }`}
                >
                  {l.label}
                  {active && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1.5 h-1.5 rounded-full bg-[#f5b300]" />
                  )}
                </Link>
              );
            })}
            <button className="px-4 py-2 text-[15px] font-medium text-[#14110f] hover:text-[#f5b300] flex items-center gap-1 transition-colors">
              {t('nav.pages')} <ChevronDown className="w-4 h-4" />
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/book"
              onClick={goToBook}
              className="hidden md:inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-6 py-3 rounded-md shadow-[0_8px_24px_-8px_rgba(245,179,0,0.65)] hover:shadow-[0_10px_28px_-6px_rgba(245,179,0,0.7)] transition-all hover:-translate-y-0.5"
            >
              <CalendarCheck className="w-4 h-4" />
              {t('cta.bookAppointment')}
            </Link>
            <div ref={langRef} className="lg:hidden relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label={t('top.language')}
                data-testid="header-lang-toggle"
                className="inline-flex items-center justify-center gap-1.5 px-3 h-11 rounded-md border border-neutral-200 text-[13px] font-bold text-[#14110f] hover:bg-[#fff7e0] hover:border-[#f5b300] transition-colors"
              >
                <Globe className="w-4 h-4 text-[#c89200]" />
                <span>{(LANGS.find((l) => l.code === lang)?.label) || lang}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 rounded-md bg-white shadow-[0_14px_30px_-10px_rgba(0,0,0,0.25)] border border-neutral-100 overflow-hidden z-50">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`header-lang-option-${l.code}`}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 text-[13px] font-medium text-left hover:bg-[#fff7e0] transition-colors ${
                        l.code === lang ? 'text-[#c89200]' : 'text-[#14110f]'
                      }`}
                    >
                      {l.label}
                      {l.code === lang && <Check className="w-3.5 h-3.5 text-[#f5b300]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="lg:hidden w-11 h-11 rounded-md border border-neutral-200 flex items-center justify-center text-[#14110f]"
              onClick={() => setOpen(!open)}
              aria-label="toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-neutral-100 bg-white">
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  className={`py-3 px-3 rounded-md text-[15px] font-medium ${
                    location.pathname === l.path ? 'bg-[#fff7e0] text-[#c89200]' : 'text-[#14110f] hover:bg-neutral-50'
                  }`}
                >
                  {l.label}
                </Link>
              ))}

              <div className="mt-3 pt-3 border-t border-neutral-100">
                <p className="px-3 mb-2 text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-[#f5b300]" /> {t('top.language') || 'Language'}
                </p>
                <div className="flex gap-2 px-3" role="radiogroup" aria-label="Language">
                  {LANGS.map((l) => {
                    const active = l.code === lang;
                    return (
                      <button
                        key={l.code}
                        role="radio"
                        aria-checked={active}
                        data-testid={`lang-mobile-${l.code}`}
                        onClick={() => setLang(l.code)}
                        className={`flex-1 py-2.5 rounded-md text-[13px] font-semibold border transition ${
                          active
                            ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f]'
                            : 'bg-white border-neutral-200 text-[#14110f] hover:border-[#f5b300]/50 hover:bg-[#fff7e0]'
                        }`}
                      >
                        {l.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!user && (
                <Link
                  to="/login"
                  data-testid="header-mobile-signin"
                  className="mt-3 inline-flex items-center justify-center gap-2 border border-neutral-200 text-[#14110f] font-semibold px-6 py-3 rounded-md hover:bg-neutral-50"
                >
                  <LogIn className="w-4 h-4" /> {t('top.signIn')}
                </Link>
              )}
              <Link
                to="/book"
                onClick={goToBook}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#f5b300] text-[#14110f] font-semibold px-6 py-3 rounded-md"
              >
                <CalendarCheck className="w-4 h-4" /> {t('cta.bookAppointment')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
